import { useState, useCallback, useRef, useEffect } from 'react';

const useVoiceActorStatus = (authToken, backendUrl) => {
  const [isVoiceActor, setIsVoiceActor] = useState(false);
  const checkRef = useRef(false);
  const resultRef = useRef(null);
  
  const checkVoiceActorStatus = useCallback(async () => {
    if (!authToken) {
      return false;
    }
    
    // Only check once per session
    if (checkRef.current) {
      return resultRef.current;
    }
    
    checkRef.current = true;
    
    try {
      const response = await fetch(`${backendUrl}/api/voice-actor/profile`, {
        headers: {}
      });
      
      const isActor = response.ok;
      resultRef.current = isActor;
      setIsVoiceActor(isActor);
      
      // Cache in session storage
      sessionStorage.setItem('voice-actor-status', String(isActor));
      sessionStorage.setItem('voice-actor-check-time', Date.now().toString());
      
      console.log(isActor ? '✅ User IS a voice actor - setting state to true' : 'ℹ️ User is not a voice actor (normal)');
      
      return isActor;
    } catch (error) {
      console.error('Failed to check voice actor status:', error);
      resultRef.current = false;
      setIsVoiceActor(false);
      return false;
    }
  }, [authToken, backendUrl]);
  
  // Check on mount, but use cache if recent
  useEffect(() => {
    if (!authToken) {
      return;
    }
    
    const cached = sessionStorage.getItem('voice-actor-status');
    const cacheTime = sessionStorage.getItem('voice-actor-check-time');
    
    // Use cache if less than 5 minutes old (reduced from 1 hour for better responsiveness)
    if (cached && cacheTime && Date.now() - parseInt(cacheTime) < 300000) {
      setIsVoiceActor(cached === 'true');
      resultRef.current = cached === 'true';
      checkRef.current = true;
      console.log('📦 Using cached voice actor status:', cached, `(cached ${Math.round((Date.now() - parseInt(cacheTime)) / 1000)}s ago)`);
    } else {
      if (cached) {
        console.log('🔄 Cache expired, rechecking voice actor status...');
      }
      checkVoiceActorStatus();
    }
  }, [checkVoiceActorStatus, authToken]);
  
  const resetVoiceActorStatus = useCallback(() => {
    checkRef.current = false;
    resultRef.current = null;
    sessionStorage.removeItem('voice-actor-status');
    sessionStorage.removeItem('voice-actor-check-time');
  }, []);
  
  return {
    isVoiceActor,
    checkVoiceActorStatus,
    resetVoiceActorStatus
  };
};

export default useVoiceActorStatus;